import { useState, useRef, useEffect } from 'react';
import { Clock, Check, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface TimeEstimateInputProps {
  value: number | null | undefined;
  onChange: (minutes: number | null) => Promise<void>;
  disabled?: boolean;
  compact?: boolean;
}

const TIME_PRESETS = [
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '4h', minutes: 240 },
  { label: '8h', minutes: 480 },
];

/**
 * Format minutes to a human-readable string
 */
function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

export function TimeEstimateInput({
  value,
  onChange,
  disabled = false,
  compact = false,
}: TimeEstimateInputProps) {
  const { isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handlePresetClick = async (minutes: number) => {
    if (disabled || isSaving) return;
    setError(null);
    setIsSaving(true);
    try {
      await onChange(minutes);
      setIsEditing(false);
    } catch {
      setError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomSubmit = async () => {
    if (disabled || isSaving) return;
    const minutes = parseInt(customValue, 10);
    if (isNaN(minutes) || minutes <= 0) {
      setError('Enter a valid number');
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await onChange(minutes);
      setIsEditing(false);
      setCustomValue('');
    } catch {
      setError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (disabled || isSaving) return;
    setError(null);
    setIsSaving(true);
    try {
      await onChange(null);
      setIsEditing(false);
    } catch {
      setError('Failed to clear');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setCustomValue('');
      setError(null);
    }
  };

  // Read-only display for non-admins
  if (!isAdmin) {
    if (value === null || value === undefined) {
      return null;
    }
    return (
      <div className={`flex items-center gap-1 text-gray-medium ${compact ? 'text-body-sm' : ''}`}>
        <Clock size={compact ? 12 : 14} />
        <span>{formatTime(value)}</span>
      </div>
    );
  }

  // Admin editing mode
  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset.minutes}
              onClick={() => handlePresetClick(preset.minutes)}
              disabled={disabled || isSaving}
              className={`px-2 py-1 text-body-sm rounded border transition-colors
                ${value === preset.minutes
                  ? 'bg-coral text-white border-coral'
                  : 'bg-white text-gray-medium border-gray-light hover:border-coral hover:text-coral'}
                disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="number"
            min="1"
            placeholder="Custom (min)"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || isSaving}
            className="w-24 px-2 py-1 text-body-sm border border-gray-light rounded focus:outline-none focus:border-coral"
          />
          <button
            onClick={handleCustomSubmit}
            disabled={disabled || isSaving || !customValue}
            className="p-1 text-status-success hover:bg-status-success/10 rounded disabled:opacity-50"
            title="Save"
          >
            <Check size={16} />
          </button>
          <button
            onClick={() => {
              setIsEditing(false);
              setCustomValue('');
              setError(null);
            }}
            disabled={isSaving}
            className="p-1 text-gray-medium hover:bg-gray-light rounded"
            title="Cancel"
          >
            <X size={16} />
          </button>
          {value && (
            <button
              onClick={handleClear}
              disabled={disabled || isSaving}
              className="px-2 py-1 text-body-sm text-status-error hover:bg-status-error/10 rounded"
            >
              Clear
            </button>
          )}
        </div>
        {error && <p className="text-status-error text-body-sm">{error}</p>}
      </div>
    );
  }

  // Admin non-editing mode - show value or "Add estimate" button
  return (
    <button
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      className={`flex items-center gap-1 transition-colors disabled:opacity-50
        ${value
          ? 'text-coral hover:text-coral-dark'
          : 'text-gray-medium hover:text-coral'}
        ${compact ? 'text-body-sm' : ''}`}
    >
      <Clock size={compact ? 12 : 14} />
      {value ? (
        <span>{formatTime(value)}</span>
      ) : (
        <span className="underline">Add estimate</span>
      )}
    </button>
  );
}
